<template>
  <canvas ref="canvas" style="display: none;"></canvas>
  <h1>Blurify</h1>
  <p id="message">{{ message }}</p>
  <p v-if="warning">{{ warning }}</p>
  <div v-if="!fileSelected" class="upload">
    <div class="slider-container">
      <button id="upload" @click="openFileSelectDialog">Select Image</button>
      <input type="file" ref="fileInputRef" style="display: none" @change="handleFileUpload" accept="image/*" />
      <div v-if="settingsRef">
        <p slider>X Components: {{ xComponents }}
          <a href="#p-components"><i class="fas fa-info-circle tool-icon"></i></a>
        </p>
        <input class="slider" type="range" id="quality-slider" v-model="xComponents" min="1" max="9">
        <p>Y Components: {{ yComponents }}
          <a href="p-components"><i class="fas fa-info-circle tool-icon"></i></a>
        </p>
        <input class="slider" type="range" id="quality-slider" v-model="yComponents" min="1" max="9">
        <p>Punch: {{ punch }} <a href="#p-punch"><i class="fas fa-info-circle tool-icon"></i></a></p>
        <input class="slider" type="range" id="quality-slider" v-model="punch" min="-5" max="7">
        <p v-if="encodeScaleComp != 'Wolt BlurHash Recommended'">Encode Scale: {{ encodeScaleComp }}% <a
            href="#p-encode"><i class="fas fa-info-circle tool-icon"></i></a></p>
        <p v-else>Encode Scale: {{ encodeScaleComp }} <a href="#p-encode"><i class="fas fa-info-circle tool-icon"></i></a>
        </p>
        <input class="slider" type="range" id="quality-slider" v-model="encodeScale" min="0" max="100">
        <p>{{ alg }} <a href="#p-alg"><i class="fas fa-info-circle tool-icon"></i></a></p>
        <input class="slider" type="range" id="quality-slider" v-model="decodeType" min="0" max="1">
        <p>Decode Scale: {{ decodeScale }}% <a href="#p-decode"><i class="fas fa-info-circle tool-icon"></i></a></p>
        <input class="slider" type="range" id="quality-slider" v-model="decodeScale" min="1" max="100">
      </div>

    </div>
    <!-- <div class="upload-container">
      <button @click="openFileSelectDialog">Upload Image</button>
      <input type="file" ref="fileInputRef" style="display: none" @change="handleFileUpload" accept="image/*" />
    </div> -->
  </div>
  <button v-if="!fileSelected" id="settings" @click="settingsRef = !settingsRef">Advanced Settings</button>
  <div v-if="fileSelected" class="container">
    <div v-if="fileSelected" @click="handleOriginalClick" class="box"
      :style="{ backgroundImage: `url(${selectedImage})` }">Original</div>
    <div v-if="fileSelected" @click="handleBlurredlClick" class="box"
      :style="{ backgroundImage: `url(${blurHashImage})` }">Blurred</div>
  </div>
  <div v-if="fileSelected">
    Download Blur:
    <button @click="downloadBlur" class="action-button" id="downloadIcon">
      <i class="fas fa-download"></i>
    </button>
    BlurHash:
    <button @click="copyBlurhash" class="action-button" id="copyBlurhashIcon">
      <i class="fas fa-copy"></i>
    </button>
    Color Average:
    <button @click="copyColor" class="action-button" id="copyColorAverageIcon">
      <i class="fas fa-copy"></i>
    </button>
    <div v-if="selectTime" class="stats-container">
      <h2>Stats for Nerds:</h2>
      <span>{{ selectTime }}</span>
      <br>
      <span>{{ encodeTime }}</span>
      <br>
      <span>{{ averageColorTime }}</span>
      <br>
      <span>{{ decodeTime }}</span>
    </div>
  </div>
  <p v-if="!fileSelected">Select an image to generate a beautifully blurred image.</p>
  <div class="text-container">
    <div class="text-content">
      
      <h2>Algorithm</h2>
      <p>Blurify uses the <a href="https://github.com/woltapp/blurhash">Wolt BlurHash</a> encode algorithm to generate a
        blur
        hash from an image. BlurHash is an algorithm that encodes an image into a short string, which can be used as a
        placeholder for the image. When you decode the string back into an image, you get a gradient of colors that
        resembles the original image. This is typically used in situations where you want to show a blurred preview of an
        image
        while the full image is loading, however I have found it works well as an artistic tool. Check out my decode benchmark tool <a href="https://blur-ad979.web.app/benchmark">here</a>.</p>
      <h2>Advanced Settings</h2>
      <p id="p-components"><strong>X and Y components</strong> represent the ammount of information retained across the X
        and Y axis.
        Increasing
        these values does not always generate a more pleasing image. Typically you would increase these values relative
        to the aspect ratio of the image. Expirement with different values to see what works best for your image.</p>
      <p id="p-punch"><strong>Punch</strong> is a design parameter that can loosely be treated as image contrast.</p>
      <p id="p-encode"><strong>Encode Scale</strong> scales the image before encoding. By default the encode scale width
        is set to 32
        pixels and the height relative to the aspect ratio, which is the
        recommended value for placeholder images. Increasing the encode scale will increase blur generation time and
        doesn't always result
        in a better looking image.</p>
      <p id="p-alg">There are two alogrithms that can be used to decode the blurhash into the final image. By default we
        use the <a href="https://github.com/mad-gooze/fast-blurhash">Fast BlurHash Decode Alogrithm</a>.
        This algorithm uses approximations during the decode process to generate the blur, decreasing decode time. In my
        testing the image differences are negligable.</p>
      <p id="p-decode"><strong>Decode Scale</strong> scales the final blur after decoding. By default the final blur
        resolution will
        match the original image.</p>
      <h2>About</h2>
      <p>Created by <a href="https://github.com/austinginn">Austin Ginn</a>.</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { encode, decode } from "blurhash";
import { decodeBlurHash, getBlurHashAverageColor } from 'fast-blurhash';
import Tooltip from '../components/Tooltip.vue';

export default {
  name: 'HomeView',
  components: {
    Tooltip
  },
  setup() {
    const message = ref('No file selected');
    const fileInputRef = ref(null);
    const fileSelected = ref(false);
    const selectedImage = ref(null);
    const blurHashImage = ref(null);
    const canvas = ref(null);
    const xComponents = ref(4);
    const yComponents = ref(3);
    const punch = ref(1);
    const encodeScale = ref(0);
    const decodeType = ref(1);
    const decodeScale = ref(100);
    const warning = ref(null);
    const selectTime = ref(null);
    const encodeTime = ref(null);
    const decodeTime = ref(null);
    const averageColorTime = ref(null);
    const settingsRef = ref(false);

    let blurHashString = null;
    let rgbAverageColor = null;
    let fileName = null;




    //computed
    const alg = computed(() => {
      if (decodeType.value == 0) {
        return 'Wolt BlurHash Decode Algorithm';
      } else {
        return 'Fast BlurHash Decode Algorithm';
      }
    });

    const encodeScaleComp = computed(() => {
      if (encodeScale.value == 0) {
        return 'Wolt BlurHash Recommended';
      } else {
        return encodeScale.value;
      }
    });



    //methods
    const handleOriginalClick = () => {
      console.log('original clicked');
    }
    const handleBlurredlClick = () => {
      console.log('blurred clicked');
    }
    const openFileSelectDialog = () => {
      fileInputRef.value.click();
    }
    const copyBlurhash = () => {
      navigator.clipboard.writeText(blurHashString);
      message.value = 'Blur hash copied to clipboard';
    }
    const copyColor = () => {
      navigator.clipboard.writeText(rgbAverageColor.join(','));
      message.value = 'Average RGB color copied to clipboard';
    }
    const downloadBlur = () => {
      //take data url and turn it into an image that can be downloaded
      const link = document.createElement('a');
      link.href = blurHashImage.value;

      //figure out type of image
      const type = blurHashImage.value.split(';')[0].split(':')[1];
      link.download = `${fileName}_blur.${type.split('/')[1]}`;
      link.click();
    }

    const handleFileUpload = async () => {
      //measure time to select file and display original
      const startTimeSelect = performance.now();

      console.log(canvas.value);
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        message.value = `Selected file: ${selectedFile.name}`;
        fileName = selectedFile.name.split('.')[0];
        fileSelected.value = true;

        //set background image of original div
        selectedImage.value = URL.createObjectURL(selectedFile);

        //measure time to select file and display original
        const endTimeSelect = performance.now();
        const resSelectTime = endTimeSelect - startTimeSelect;
        if (resSelectTime > 1000) {
          selectTime.value = `Select time: ${Math.round((resSelectTime / 1000) * 100) / 100}s`;
        } else {
          selectTime.value = `Select time: ${Math.round(resSelectTime * 100) / 100}ms`;
        }

        //measure time to encode blur hash
        const startEncodeTime = performance.now();

        //warn for high resolution or high quality selection
        message.value = "Generating blur hash..."
        if (selectedFile.size > 1000000) {
          warning.value = 'Warning: Large file selected. This may take a while to process.';
        }

        //generate blur hash
        const results = await genBlurHash(selectedFile);

        //measure time to get average color from blurhash
        const startColorTime = performance.now();

        //get avg color from blur hash
        rgbAverageColor = getBlurHashAverageColor(results.blurHash);

        //measure time to get average color from blurhash
        const endColorTime = performance.now();
        const resColorTime = endColorTime - startColorTime;
        if (resColorTime > 1000) {
          averageColorTime.value = `Color average time: ${Math.round((resColorTime / 1000) * 100) / 100}s`;
        } else {
          averageColorTime.value = `Color average time: ${Math.round(resColorTime * 100) / 100}ms`;
        }

        //set background color of body to average color of blur hash
        document.body.style.transition = 'background-color 1.5s ease-in-out';
        document.body.style.backgroundColor = `rgb(${rgbAverageColor.join(',')})`;


        //measure time to encode blur hash
        const endEncodeTime = performance.now();
        const resEncodeTime = endEncodeTime - startEncodeTime;
        if (resEncodeTime > 1000) {
          encodeTime.value = `Encode time: ${Math.round((resEncodeTime / 1000) * 100) / 100}s`;
        } else {
          encodeTime.value = `Encode time: ${Math.round(resEncodeTime * 100) / 100}ms`;
        }

        //display blur hash
        const blurHash = results.blurHash;
        blurHashString = blurHash;
        message.value = null;

        //measure time to decode blur hash
        const startDecodeTime = performance.now();

        //set background image of blurred div
        if (decodeType.value == 0) {
          //slow blur decode
          blurHashImage.value = await showBlur(blurHash, results.width, results.height, results.scaleWidth, results.scaleHeight, selectedFile.type);
        } else {
          //fast blur decode
          blurHashImage.value = await showBlurFast(blurHash, results.width, results.height, results.scaleWidth, results.scaleHeight, selectedFile.type);
        }

        //measure time to decode blur hash
        const endDecodeTime = performance.now();
        const resDecodeTime = endDecodeTime - startDecodeTime;
        if (resDecodeTime > 1000) {
          decodeTime.value = `Decode time: ${Math.round((resDecodeTime / 1000) * 100) / 100}s`;
        } else {
          decodeTime.value = `Decode time: ${Math.round(resDecodeTime * 100) / 100}ms`;
        }

        //reset warnings
        warning.value = null;


      } else {
        message.value = 'No file selected';
      }
    }

    ///////HELPERS///////
    const loadImage = async src =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (...args) => reject(args);
        img.src = src;
      });

    const readFile = async file =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (...args) => reject(args);
        reader.readAsDataURL(file);
      });

    const genBlurHash = async (file) => {
      const fileResult = await readFile(file);
      const image = await loadImage(fileResult);
      let scaleWidth = 0;
      let scaleHeight = 0;

      //scale
      if (encodeScale.value == 0) {
        //set scaled width based on WOLT BlurHash sugggested 
        scaleWidth = 32;
        //set height to be proportional to width
        scaleHeight = Math.floor((image.height / image.width) * scaleWidth);
      } else {
        scaleWidth = Math.floor(image.width * (encodeScale.value / 100));
        scaleHeight = Math.floor(image.height * (encodeScale.value / 100));
      }


      const canvas = document.createElement('canvas');
      canvas.width = scaleWidth;
      canvas.height = scaleHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, scaleWidth, scaleHeight);
      const imageData = ctx.getImageData(0, 0, scaleWidth, scaleHeight);
      const blurHash = await encode(imageData.data, imageData.width, imageData.height, xComponents.value, yComponents.value);
      const height = image.height;
      const width = image.width;

      return { blurHash, width, height, scaleWidth, scaleHeight };
    }

    const showBlur = async (blurHash, width, height, scaleWidth, scaleHeight, type) => {
      //decode the hash and get pixels
      console.log({ width, height, scaleWidth, scaleHeight });
      const pixels = await decode(blurHash, scaleWidth, scaleHeight, punch.value);

      //generate canvas
      const canvasElement = canvas.value;
      const context = canvasElement.getContext('2d');

      //set width and height to match original image
      canvasElement.width = scaleWidth;
      canvasElement.height = scaleHeight;

      const imageData = context.createImageData(scaleWidth, scaleHeight);
      imageData.data.set(pixels);
      context.putImageData(imageData, 0, 0);

      // set to new scaled canvas
      const decodeScaleWidth = Math.floor(width * (decodeScale.value / 100));
      const decodeScaleHeight = Math.floor(height * (decodeScale.value / 100));

      const newCanvas = document.createElement('canvas');
      newCanvas.width = decodeScaleWidth;
      newCanvas.height = decodeScaleHeight;

      const newContext = newCanvas.getContext('2d');
      newContext.drawImage(canvasElement, 0, 0, decodeScaleWidth, decodeScaleHeight);

      const newDataURL = newCanvas.toDataURL(type);
      console.log(newDataURL);

      return newDataURL;
    }

    const showBlurFast = async (blurHash, width, height, scaleWidth, scaleHeight, type) => {
      //decode the hash and get pixels
      console.log({ width, height, scaleWidth, scaleHeight });
      const pixels = await decodeBlurHash(blurHash, scaleWidth, scaleHeight, punch.value);

      //generate canvas
      const canvasElement = canvas.value;
      const context = canvasElement.getContext('2d');

      //set width and height to match original image
      canvasElement.width = scaleWidth;
      canvasElement.height = scaleHeight;

      const imageData = context.createImageData(scaleWidth, scaleHeight);
      imageData.data.set(pixels);
      context.putImageData(imageData, 0, 0);

      // set to new scaled canvas
      const decodeScaleWidth = Math.floor(width * (decodeScale.value / 100));
      const decodeScaleHeight = Math.floor(height * (decodeScale.value / 100));

      const newCanvas = document.createElement('canvas');
      newCanvas.width = decodeScaleWidth;
      newCanvas.height = decodeScaleHeight;

      const newContext = newCanvas.getContext('2d');
      newContext.drawImage(canvasElement, 0, 0, decodeScaleWidth, decodeScaleHeight);

      const newDataURL = newCanvas.toDataURL(type);
      console.log(newDataURL);

      return newDataURL;
    }

    return {
      handleOriginalClick, handleBlurredlClick, openFileSelectDialog, handleFileUpload, copyBlurhash, copyColor, downloadBlur, averageColorTime, encodeScaleComp, settingsRef, alg, encodeScale, decodeScale, decodeType, selectTime, encodeTime, decodeTime, warning, xComponents, yComponents, punch, message, fileInputRef, fileSelected, selectedImage, blurHashImage, canvas
    }
  }
}
</script>
<style>
/* Use flexbox to arrange divs side by side on desktop and vertically on mobile */
.container {
  text-align: center;
  padding: 20px;
  padding-top: 0;
  height: 100%;
  /* Set container height to 100% of the viewport height */
  display: flex;
  justify-content: center;
}

h1 {
  margin: 0;
  padding: 1%;
  font-size: 44px;
}

#message {
  margin-top: 0;
}

.box {
  width: 48%;
  /* 2% margin for spacing */
  padding: 3%;
  /* 16x9 aspect ratio padding */
  border-radius: 15px;
  /* Rounded edges */

  /* Background color for the divs */
  background-color: #0f1011;
  margin: 3%;
  margin-top: 0;
  /* margin-bottom: 0; */
  /* Add some margin between divs */
  text-align: center;
  box-sizing: border-box;
  height: 100%;
  /* Set box height to 100% of the container height */
  background-size: cover;
  background-position: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

}

button {
  margin: auto;
  /* Center the button horizontally within the .box */
  padding: 10px 20px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 15px;
  cursor: pointer;
}

input[type="file"] {
  display: none;
}

/* Media query to stack divs vertically on mobile devices */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .text-container {
    width: 100%;
    margin-top: 5%;
    margin: 0 auto;
    text-align: left;
    padding-bottom: 20px;
    /* Full width on mobile */
  }

  .box {
    width: 100%;
    margin: 0;
    margin-top: 5%;
    /* Full width on mobile */

  }
}

@media screen and (min-width: 769px) {

  /* Your styles for screens with a width greater than 768px */
  .text-container {
    width: 50%;
    /* Adjust the width as per your requirement */
    margin: 0 auto;
    text-align: left;
    padding-bottom: 20px;
  }
}

.upload-container {
  text-align: center;
  display: inline-block;
}

#upload {
  margin-top: 10px;
}

.slider-container {
  width: 300px;
  text-align: center;
  /* margin: 0 auto; */
  display: inline-block
}

.slider-container p {
  margin-bottom: 2px;
}

.slider {
  margin-top: 2px;
}

#quality-slider {
  width: 100%;
  /* background: linear-gradient(to right, #f00 0%, #0f0 100%); */
  border: 1px solid #ccc;
  border-radius: 5px;
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
}

#quality-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #0077cc;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -5px;
}

#slider-value {
  display: block;
  font-size: 1.5em;
  /* margin-top: 10px; */
  color: #0077cc;
}

.action-icons {
  /* display: flex;
  justify-content: space-between; */
  width: 200px;
  margin: 0 auto;
  font-size: 1.5em;
}

.action-button {
  background: none;
  border: none;
  cursor: pointer;
  padding-left: 4px;
}

.action-button i {
  font-size: 24px;
}

.action-button:focus {
  outline: none;
}

#settings {
  margin-top: 10px;
}

.text-content{
  padding: 20px;
}



a {
  color: #0077cc;
}
</style>
